import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Shield, Bell, Sparkles, Calculator, FileCheck, Radar, ArrowRight, Check, AlertCircle } from "lucide-react";
import Logo from "@/components/Logo";

const features = [
  { icon: Shield, title: "Sempre sulla stessa pagina", desc: "Vedi i tuoi calcoli, le tue scadenze, i tuoi documenti. Tutto in un posto, sempre chiaro." },
  { icon: Radar, title: "Radar bandi e finanziamenti", desc: "Un agente AI monitora ogni giorno bandi statali e regionali per il tuo settore e ti avvisa." },
  { icon: Sparkles, title: "Assistente fiscale AI", desc: "Chiedi qualsiasi cosa. Risposte chiare, spiegate come le vorresti sentire." },
  { icon: Calculator, title: "Simulatori fiscali", desc: "IRPEF, IVA, Forfettario, ISEE, Bollo — verifica se i numeri tornano." },
  { icon: Bell, title: "Scadenzario intelligente", desc: "Tutte le tue scadenze in un posto, con reminder. Niente sorprese." },
  { icon: FileCheck, title: "Archivio e documenti", desc: "Fatture, ricevute, certificati e autocertificazioni pronte in 30 secondi." },
];

export default function Landing() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-40 bg-background/70 backdrop-blur-xl border-b border-border/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="nav-home">
            <Logo iconSize={52} textClassName="text-3xl" />
          </Link>
          <div className="flex items-center gap-3">
            <Link to="/login"><Button variant="ghost" className="rounded-full" data-testid="nav-login">Accedi</Button></Link>
            <Link to="/register"><Button className="rounded-full hover:-translate-y-0.5 transition-transform" data-testid="nav-register">Inizia gratis</Button></Link>
          </div>
        </div>
      </header>

      <section className="relative overflow-hidden">
        <div className="grain-overlay" />
        <div className="max-w-7xl mx-auto px-6 py-20 lg:py-28 grid lg:grid-cols-2 gap-12 items-center">
          <div className="fade-in-up">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-accent text-accent-foreground text-xs font-bold uppercase tracking-widest mb-6">
              <AlertCircle className="w-3 h-3 text-secondary" /> Il tuo fisco, finalmente chiaro.
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl lg:text-6xl tracking-tighter leading-[1.05] mb-6">
              Il tuo<br />
              <span className="text-primary">copilota fiscale.</span>
            </h1>
            <p className="text-lg text-muted-foreground max-w-lg leading-relaxed mb-4">
              TUOTAX non sostituisce il tuo commercialista —
              ti dà gli strumenti per <strong>verificare che tutto sia corretto</strong>, monitorare bandi e opportunità che ti spettano,
              e non farti scoprire in ritardo.
            </p>
            <p className="text-sm text-muted-foreground mb-8">
              Simulatori, scadenze, guide, radar bandi e un AI che risponde in italiano. Tutto in un posto.
            </p>
            <div className="flex flex-wrap gap-3">
              <Link to="/register">
                <Button size="lg" className="rounded-full h-12 px-8 hover:-translate-y-0.5 hover:shadow-md transition-all" data-testid="hero-cta">
                  Inizia gratis <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </Link>
              <Link to="/login">
                <Button size="lg" variant="outline" className="rounded-full h-12 px-8" data-testid="hero-login">Ho già un account</Button>
              </Link>
            </div>
            <div className="mt-8 flex flex-wrap gap-6 text-sm text-muted-foreground">
              {["Verifica calcoli", "Radar bandi", "AI in italiano"].map((t) => (
                <span key={t} className="flex items-center gap-2"><Check className="w-4 h-4 text-primary" /> {t}</span>
              ))}
            </div>
          </div>
          <div className="relative fade-in-up" style={{ animationDelay: "0.15s" }}>
            <div className="absolute -inset-4 bg-secondary/10 rounded-[2rem] rotate-2" />
            <div className="relative rounded-[2rem] overflow-hidden shadow-2xl h-[520px]">
              <img
                src="https://images.unsplash.com/photo-1522071820081-009f0129c71c?w=1400&q=80&auto=format&fit=crop"
                alt="Team di giovani professionisti che collaborano in modo rilassato"
                className="absolute inset-0 w-full h-full object-cover"
                loading="eager"
              />
              {/* Overlay verde bosco per uniformità brand */}
              <div
                className="absolute inset-0 mix-blend-multiply"
                style={{ background: "hsl(155 27% 25% / 0.22)" }}
              />
              <div
                className="absolute inset-0"
                style={{ background: "linear-gradient(160deg, hsl(155 27% 20% / 0.12) 0%, transparent 45%, hsl(14 69% 55% / 0.08) 100%)" }}
              />
              {/* Grain */}
              <div className="absolute inset-0 opacity-25 mix-blend-overlay grain-overlay pointer-events-none" />
            </div>
            <Card className="absolute -bottom-6 -left-6 p-4 rounded-2xl shadow-xl bg-card border max-w-[260px] hidden md:block">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-secondary/20 flex items-center justify-center">
                  <Radar className="w-5 h-5 text-secondary" />
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Nuovo alert bandi</div>
                  <div className="font-semibold text-sm">3 opportunità per il tuo settore</div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Problem framing */}
      <section className="max-w-5xl mx-auto px-6 py-16">
        <Card className="p-8 lg:p-12 rounded-3xl border-2 border-primary/10 bg-accent/40">
          <div className="grid md:grid-cols-3 gap-8 items-center">
            <div className="md:col-span-2">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-3">Perché nasce TUOTAX</div>
              <h2 className="font-heading font-bold text-2xl lg:text-3xl tracking-tight mb-3">
                Il fisco italiano è complicato.<br />
                TUOTAX lo rende semplice.
              </h2>
              <p className="text-muted-foreground leading-relaxed">
                Scadenze da ricordare, F24 da controllare, detrazioni da non perdere, bandi a cui partecipare.
                TUOTAX ti dà <strong className="text-foreground">una visione chiara e completa</strong> della tua situazione fiscale,
                per affrontare ogni scadenza con serenità — al fianco del tuo commercialista, mai al suo posto.
              </p>
            </div>
            <div className="text-center md:text-right">
              <div className="font-heading font-bold text-6xl text-primary">1 su 3</div>
              <div className="text-sm text-muted-foreground mt-1">contribuenti riceve un avviso bonario almeno una volta</div>
            </div>
          </div>
        </Card>
      </section>

      <section className="max-w-7xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <div className="text-xs font-bold uppercase tracking-[0.2em] text-secondary mb-3">Cosa fa per te</div>
          <h2 className="font-heading font-bold text-3xl lg:text-4xl tracking-tight">
            Tutto quello che serve per <span className="text-primary">non farti trovare impreparato</span>.
          </h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((f, i) => (
            <Card key={f.title} className="p-6 rounded-2xl border bg-card shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:-translate-y-1 hover:shadow-lg transition-transform fade-in-up" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`feature-${i}`}>
              <div className="w-12 h-12 rounded-xl bg-accent flex items-center justify-center mb-4">
                <f.icon className="w-6 h-6 text-primary" />
              </div>
              <h3 className="font-heading font-semibold text-xl mb-2">{f.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{f.desc}</p>
            </Card>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 pb-24">
        <Card className="p-10 lg:p-16 rounded-3xl bg-primary text-primary-foreground border-0 relative overflow-hidden">
          <div className="grain-overlay opacity-20" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading font-bold text-3xl lg:text-5xl tracking-tight mb-4">
              Prendi il controllo del tuo fisco.
            </h2>
            <p className="text-primary-foreground/80 text-lg mb-8">
              Registrati in 60 secondi, dicci di che regione sei e cosa fai, e il tuo radar AI inizia subito a lavorare per te.
            </p>
            <Link to="/register">
              <Button size="lg" className="rounded-full h-12 px-8 bg-secondary text-secondary-foreground hover:bg-secondary/90" data-testid="cta-bottom">
                Inizia gratis <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </Link>
          </div>
        </Card>
      </section>

      <footer className="border-t border-border/60 py-8">
        <div className="max-w-7xl mx-auto px-6 flex flex-wrap items-center justify-between gap-4 text-sm text-muted-foreground">
          <div>© 2026 TUOTAX — Il tuo copilota fiscale.</div>
          <div className="flex gap-4 items-center">
            <Link to="/privacy" className="hover:text-primary transition-colors" data-testid="footer-privacy">Privacy</Link>
            <span>·</span>
            <Link to="/terms" className="hover:text-primary transition-colors" data-testid="footer-terms">Termini</Link>
            <span>·</span>
            <span>Non sostituiamo il commercialista.</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
